import { APP_PERMISSIONS } from '@exam-center/permission';
import {
  Alert,
  Box,
  Flex,
  FormButton,
  FormSearchSelection,
  FormTextField,
  PaperSection,
  Radio,
  RadioGroup,
  ReadonlyField,
  Text,
} from '@shared/components';
import { usePermission, validatePermissions } from '@shared/permissions';

import {
  useExamLocationRequestForm,
  useExamLocationRequestDisplayData,
} from '../../providers/exam-location-request-form-provider';

export default function ExamLocationRequestForm() {
  const {
    locationMode,
    setLocationMode,
    buildingMode,
    setBuildingMode,
    provinceOptions,
    locationOptions,
    buildingOptions,
    isProvinceSelected,
    isLocationSelected,
    submitting,
    isUpdateMode,
    canSubmit,
  } = useExamLocationRequestForm();

  // Separate hook for display data (only used for readonly views)
  const displayData = useExamLocationRequestDisplayData();

  const { permissions } = usePermission();
  const hasUpdatePermission = validatePermissions(
    APP_PERMISSIONS.EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION_REQUEST.UPDATE,
    permissions,
  );

  return (
    <>
      <PaperSection title='ข้อมูลสถานที่สอบ'>
        <Flex
          sx={(theme) => ({
            padding: theme.spacing(6),
            flexDirection: 'column',
            gap: theme.spacing(4),
          })}
        >
          {hasUpdatePermission ? (
            <Alert severity='info'>
              ชื่อสถานที่/หน่วยงานที่กรอกจะถูกนำไปใช้แสดงบนใบรับรองผลการสอบ (Certificate)
              กรุณากรอกให้ถูกต้องทั้งภาษาไทยและภาษาอังกฤษ
            </Alert>
          ) : null}
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '240px' }}>
              จังหวัด
            </Text>
            {hasUpdatePermission ? (
              <FormSearchSelection
                name='provinceId'
                options={provinceOptions}
                sx={{ width: '100%' }}
                label='จังหวัด'
                required
                placeholder='เลือกจังหวัด'
              />
            ) : (
              <ReadonlyField label='จังหวัด' content={displayData.province?.nameTh || ''} />
            )}
          </Flex>

          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              width: '100%',
            })}
          >
            <Text variant='body1Semibold' sx={{ minWidth: '240px' }}>
              สถานที่/หน่วยงาน/อาคาร
            </Text>

            <RadioGroup
              name='locationMode'
              value={locationMode}
              onChange={(_, value: string) => {
                setLocationMode(value as 'existing' | 'new' | '');
              }}
              sx={(theme) => ({
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(4),
              })}
            >
              <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(2) }}>
                <Radio
                  label='เลือกอาคารที่มีในระบบ'
                  value='existing'
                  disabled={!isProvinceSelected || !hasUpdatePermission}
                  sx={{ height: '24px' }}
                />
                {locationMode === 'existing' ? (
                  <Flex
                    sx={(theme) => ({
                      ml: theme.spacing(6),
                      flexDirection: 'column',
                      gap: theme.spacing(3),
                      p: theme.spacing(4),
                      borderRadius: theme.shape.rounded(2),
                      bgcolor: theme.palette.grey[50],
                      border: `1px solid`,
                      borderColor: theme.palette.divider,
                    })}
                  >
                    {hasUpdatePermission ? (
                      <FormSearchSelection
                        name='locationId'
                        options={locationOptions}
                        label='สถานที่/หน่วยงาน (ภาษาไทย)'
                        required
                        placeholder='เลือกสถานที่หรือหน่วยงาน (ภาษาไทย)'
                        helperText='เช่น ธนาคาร มหาวิทยาลัย ศูนย์ฝึกอบรม ฯลฯ (กรณีมีหลายสาขาหรือวิทยาเขต กรุณาระบุให้ครบถ้วน)'
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <ReadonlyField
                        label='สถานที่/หน่วยงาน (ภาษาไทย)'
                        content={displayData.location?.nameTh || ''}
                      />
                    )}
                    <ReadonlyField
                      label='สถานที่/หน่วยงาน (ภาษาอังกฤษ)'
                      content={displayData.location?.nameEn || '-'}
                    />
                    <RadioGroup
                      name='buildingMode'
                      value={buildingMode}
                      onChange={(_, value: string) => {
                        setBuildingMode(value as 'existing' | 'new' | '');
                      }}
                      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
                    >
                      <Flex sx={{ gap: 4, flexDirection: 'row' }}>
                        <Radio
                          label='เลือกจากรายการที่มีในระบบ'
                          value='existing'
                          disabled={
                            !isProvinceSelected || !isLocationSelected || !hasUpdatePermission
                          }
                        />
                        <Radio
                          label='เพิ่มอาคารใหม่'
                          value='new'
                          disabled={
                            !isProvinceSelected || !isLocationSelected || !hasUpdatePermission
                          }
                        />
                      </Flex>
                      {buildingMode === 'existing' ? (
                        hasUpdatePermission ? (
                          <FormSearchSelection
                            name='buildingId'
                            options={buildingOptions}
                            label='อาคาร'
                            required
                            placeholder='เลือกอาคาร'
                            disabled={!isProvinceSelected || !isLocationSelected}
                          />
                        ) : (
                          <ReadonlyField label='อาคาร' content={displayData.building?.name || ''} />
                        )
                      ) : null}
                      {buildingMode === 'new' ? (
                        hasUpdatePermission ? (
                          <FormTextField
                            name='buildingName'
                            label='อาคาร'
                            placeholder='กรอกอาคาร'
                            required
                            sx={{ width: '100%' }}
                          />
                        ) : (
                          <ReadonlyField label='อาคาร' content={displayData.building?.name || ''} />
                        )
                      ) : null}
                    </RadioGroup>
                  </Flex>
                ) : null}
              </Flex>

              <Flex
                sx={{
                  flexDirection: 'column',
                  gap: (theme) => theme.spacing(2),
                }}
              >
                <Radio
                  label='เพิ่มสถานที่/หน่วยงาน/อาคารใหม่'
                  value='new'
                  disabled={!isProvinceSelected || !hasUpdatePermission}
                  sx={{ height: '24px' }}
                />
                {locationMode === 'new' ? (
                  <Flex
                    sx={(theme) => ({
                      ml: theme.spacing(6),
                      flexDirection: 'column',
                      gap: theme.spacing(3),
                      p: theme.spacing(3),
                      borderRadius: theme.shape.rounded(2),
                      bgcolor: theme.palette.action.hover,
                      border: `1px solid`,
                      borderColor: theme.palette.divider,
                    })}
                  >
                    {hasUpdatePermission ? (
                      <FormTextField
                        name='locationNameTh'
                        label='สถานที่/หน่วยงาน (ภาษาไทย)'
                        placeholder='ระบุชื่อสถานที่/หน่วยงาน'
                        helperText='เช่น ธนาคาร มหาวิทยาลัย ศูนย์ฝึกอบรม ฯลฯ (กรณีมีหลายสาขาหรือวิทยาเขต กรุณาระบุให้ครบถ้วน)'
                        required
                        disabled={!hasUpdatePermission}
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <ReadonlyField
                        label='สถานที่/หน่วยงาน (ภาษาไทย)'
                        content={displayData.location?.nameTh || ''}
                      />
                    )}

                    {hasUpdatePermission ? (
                      <FormTextField
                        name='locationNameEn'
                        label='สถานที่/หน่วยงาน (ภาษาอังกฤษ)'
                        placeholder='ระบุชื่อสถานที่/หน่วยงาน'
                        helperText='เช่น ธนาคาร มหาวิทยาลัย ศูนย์ฝึกอบรม ฯลฯ (กรณีมีหลายสาขาหรือวิทยาเขต กรุณาระบุให้ครบถ้วน)'
                        disabled={!hasUpdatePermission}
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <ReadonlyField
                        label='สถานที่/หน่วยงาน (ภาษาอังกฤษ)'
                        content={displayData.location?.nameEn || ''}
                      />
                    )}

                    {hasUpdatePermission ? (
                      <FormTextField
                        name='buildingName'
                        label='อาคาร'
                        placeholder='กรอกอาคาร'
                        required
                        disabled={!hasUpdatePermission}
                        sx={{ width: '100%' }}
                      />
                    ) : (
                      <ReadonlyField label='อาคาร' content={displayData.building?.name || ''} />
                    )}
                  </Flex>
                ) : null}
              </Flex>
            </RadioGroup>
          </Flex>
        </Flex>
      </PaperSection>
      {!isUpdateMode ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <FormButton label='บันทึก' type='submit' loading={submitting} disabled={!canSubmit} />
        </Box>
      ) : null}
    </>
  );
}
